import { TIME_CONTROLS } from '../chess/game.js';

const DAY_MS = 24 * 60 * 60 * 1000;
const DUPLICATE_WINDOW_MS = 30 * DAY_MS;
const DIVERSITY_TARGET = 30;

const BASE_TIME_MS = Object.fromEntries(
  Object.entries(TIME_CONTROLS).map(([key, control]) => [
    key,
    control.perMoveMs ?? control.initialMs
  ])
);

function hasPlayer(certificate, playerId) {
  return certificate.players?.some((player) => player.playerId === playerId);
}

export function opponentId(certificate, playerId) {
  return certificate.players?.find((player) => player.playerId !== playerId)?.playerId || null;
}

export function transcriptSimilarity(leftMoves, rightMoves) {
  if (leftMoves.length === 0 && rightMoves.length === 0) {
    return 1;
  }
  const longest = Math.max(leftMoves.length, rightMoves.length);
  let positionalMatches = 0;
  for (let index = 0; index < Math.min(leftMoves.length, rightMoves.length); index += 1) {
    if (leftMoves[index] === rightMoves[index]) {
      positionalMatches += 1;
    }
  }
  return positionalMatches / longest;
}

export function detectDuplicateTranscripts(
  records,
  { threshold = 0.9, windowMs = DUPLICATE_WINDOW_MS } = {}
) {
  const ordered = [...records].sort(
    (left, right) => left.certificate.endTs - right.certificate.endTs
  );
  const duplicates = [];

  for (let laterIndex = 1; laterIndex < ordered.length; laterIndex += 1) {
    const later = ordered[laterIndex];
    for (let earlierIndex = laterIndex - 1; earlierIndex >= 0; earlierIndex -= 1) {
      const earlier = ordered[earlierIndex];
      if (later.certificate.endTs - earlier.certificate.endTs > windowMs) {
        break;
      }
      const sharedPlayer = later.certificate.players.find((player) =>
        hasPlayer(earlier.certificate, player.playerId)
      );
      if (!sharedPlayer) {
        continue;
      }
      const similarity = transcriptSimilarity(earlier.sanMoves, later.sanMoves);
      if (similarity >= threshold) {
        duplicates.push({
          certificateHash: later.certificate.certificateHash,
          originalHash: earlier.certificate.certificateHash,
          sharedPlayerId: sharedPlayer.playerId,
          similarity
        });
        break;
      }
    }
  }

  return duplicates;
}

export function sameOpponentFactor(certificates, playerId, targetOpponentId, now) {
  const gamesToday = certificates.filter(
    (certificate) =>
      hasPlayer(certificate, playerId)
      && opponentId(certificate, playerId) === targetOpponentId
      && certificate.endTs >= now - DAY_MS
      && certificate.endTs <= now
  ).length;

  if (gamesToday < 3) {
    return 1;
  }
  if (gamesToday >= 7) {
    return 0;
  }
  return (7 - gamesToday) / 5;
}

export function opponentDiversityFactor(certificates, playerId) {
  const recent = certificates
    .filter((certificate) => hasPlayer(certificate, playerId))
    .sort((left, right) => right.endTs - left.endTs)
    .slice(0, 50);
  if (recent.length === 0) {
    return 1;
  }

  const counts = new Map();
  recent.forEach((certificate) => {
    const opponent = opponentId(certificate, playerId);
    counts.set(opponent, (counts.get(opponent) || 0) + 1);
  });
  if (counts.size === 1) {
    return 1 / DIVERSITY_TARGET;
  }

  const entropy = [...counts.values()].reduce((sum, count) => {
    const probability = count / recent.length;
    return sum - probability * Math.log(probability);
  }, 0);
  return Math.min(1, entropy / Math.log(DIVERSITY_TARGET));
}

export function noveltyFactor(certificates, playerId, targetOpponentId, now) {
  return (
    sameOpponentFactor(certificates, playerId, targetOpponentId, now)
    * opponentDiversityFactor(certificates, playerId)
  );
}

export function validateMinimumSubstance(
  certificate,
  { verifiedDurationMs = certificate.endTs - certificate.startTs } = {}
) {
  const control = TIME_CONTROLS[certificate.timeControl];
  const baseTimeMs = BASE_TIME_MS[certificate.timeControl];
  const untimed = control?.clockMode === 'none';
  const reasons = [];
  if (!control || (!baseTimeMs && !untimed)) {
    reasons.push('unsupported-time-control');
  }
  if (certificate.moveCount < 10) {
    reasons.push('fewer-than-10-plies');
  }
  if (baseTimeMs && verifiedDurationMs < baseTimeMs * 0.2) {
    reasons.push('insufficient-duration');
  }
  return {
    valid: reasons.length === 0,
    reasons,
    requiredDurationMs: baseTimeMs ? baseTimeMs * 0.2 : null,
    verifiedDurationMs
  };
}

function resultFactor(certificate, playerId) {
  const whiteId = certificate.players[0].playerId;
  if (certificate.result === '½-½') {
    return 0.6;
  }
  const winnerId = certificate.result === '1-0'
    ? whiteId
    : certificate.players[1].playerId;
  return winnerId === playerId ? 1 : 0.25;
}

export function calculateMatchPoints({
  certificate,
  playerId,
  playerRating = 1500,
  opponentRating = 1500,
  novelty = 1,
  substance = validateMinimumSubstance(certificate),
  duplicate = false,
  basePoints = 10
}) {
  if (!certificate.rated || duplicate || !substance.valid || !hasPlayer(certificate, playerId)) {
    return {
      points: 0,
      eligible: false,
      reason: duplicate
        ? 'duplicate-transcript'
        : !certificate.rated
          ? 'unrated'
          : !substance.valid
            ? substance.reasons[0]
            : 'not-a-player'
    };
  }

  const opponentStrength = Math.max(
    0.5,
    Math.min(1.5, 1 + (opponentRating - playerRating) / 800)
  );
  const points =
    basePoints
    * resultFactor(certificate, playerId)
    * opponentStrength
    * Math.max(0, Math.min(1, novelty));
  return {
    points: Math.round(points * 100) / 100,
    eligible: true,
    factors: {
      result: resultFactor(certificate, playerId),
      opponentStrength,
      novelty: Math.max(0, Math.min(1, novelty))
    }
  };
}
